import type { ReactNode } from "react";
import { cn } from "../lib/utils.js";

/**
 * A page section: friendly heading with an optional count, content below.
 * No chrome around the section itself — the cards inside carry the borders.
 */
export function Rack({
  label,
  hint,
  count,
  actions,
  children,
  className,
  trayClassName,
}: {
  /** Omit when the page's h1 already names this content. */
  label?: string;
  /** Native title tooltip on the heading — for context that doesn't need to sit on the page permanently. */
  hint?: string;
  count?: number;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  trayClassName?: string;
}) {
  return (
    // Column layout so a Rack in a two-up grid can stretch: the heading keeps its
    // height and the tray takes the rest, which is what makes paired tiles end
    // level regardless of how many rows each one has.
    <section className={cn("flex flex-col", className)}>
      {label !== undefined ? (
        <div className="mb-3 flex items-baseline gap-2">
          <h2 className="text-lg font-bold tracking-tight" title={hint}>
            {label}
          </h2>
          {count !== undefined ? (
            <span className="text-sm tabular-nums text-muted-foreground">{count}</span>
          ) : null}
          <div className="ml-auto flex items-center gap-2">{actions}</div>
        </div>
      ) : null}
      <div className={cn("flex-1", trayClassName)}>{children}</div>
    </section>
  );
}
